import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  CalendarIcon,
  ClockIcon,
  BuildingStorefrontIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  EllipsisVerticalIcon,
  StarIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
  RectangleStackIcon,
} from '@heroicons/react/24/outline';
import { fetchMedicalStoreProfile, fetchMedicalStoreOrders, fetchVendorMedicineInvoiceBlob } from '../../../services/MasterAdmin/medicalStoreService';
import { getFeedbacks } from '../../../services/MasterAdmin/feedbackService';
import { toggleVendorStatus, fetchVendorCommissionRate, updateVendorCommissionRate } from '../../../services/MasterAdmin/vendorService';
import { MedicalStoreProfile } from '../../../models/MasterAdmin/medicalStore';
import { OrderStats, type Order } from '../../../models/MasterAdmin/order';
import type { User } from '../../../models/MasterAdmin/user';
import type { FeedbackRow } from '../../../services/MasterAdmin/feedbackService';
import { format } from 'date-fns';
import VendorBankDetailsModal from '../../../components/MasterAdmin/VendorBankDetailsModal';

interface MetricCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  trend?: string;
  trendValue?: string;
  description?: string;
}

type OrderWithCustomerAlias = Order & { user?: User | null; patientName?: string | null };

function getCustomerDisplay(order: OrderWithCustomerAlias) {
  const u = order.User ?? order.user;
  return {
    name: u?.name ?? order.patientName ?? '—',
    phone: u?.phone_number ?? '—',
  };
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, icon, trend, trendValue, description }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 sm:p-6 hover:shadow-md transition-all duration-300 border border-gray-100 dark:border-gray-700">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{title}</p>
        <p className="text-2xl font-semibold text-gray-900 dark:text-white mt-2">{value}</p>
        {trend && (
          <p className={`text-sm mt-2 ${trend === 'up' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
            {trendValue}
          </p>
        )}
        {description && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{description}</p>
        )}
      </div>
      <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg">{icon}</div>
    </div>
  </div>
);

const MEDICINE_INVOICE_ELIGIBLE_STATUSES = new Set([
  'payment_completed',
  'ready_to_pickup',
  'out_for_delivery',
  'delivered',
  'refund_requested',
  'refund_completed',
  'replacement_requested',
  'replacement_delivered',
]);

const MedicalStoreVendorDashboard: React.FC = () => {
  const { vendorId } = useParams<{ vendorId: string }>();
  const navigate = useNavigate();
  const [storeData, setStoreData] = useState<MedicalStoreProfile | null>(null);
  const [orderStats, setOrderStats] = useState<OrderStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);
  const [isCommissionModalOpen, setIsCommissionModalOpen] = useState(false);
  const [commissionRateInput, setCommissionRateInput] = useState<string>('');
  const [referralCommissionInput, setReferralCommissionInput] = useState<string>('');
  const [commissionLoading, setCommissionLoading] = useState(false);
  const [commissionSaving, setCommissionSaving] = useState(false);
  const [commissionError, setCommissionError] = useState<string | null>(null);
  const [isBankDetailsModalOpen, setIsBankDetailsModalOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [vendorFeedback, setVendorFeedback] = useState<FeedbackRow[]>([]);

  useEffect(() => {
    if (!vendorId) return;
    const loadProfile = async () => {
      try {
        setLoading(true);
        const profileResponse = await fetchMedicalStoreProfile(vendorId);
        setStoreData(profileResponse);
      } catch (err) {
        console.error('Error loading profile:', err);
        setError('Failed to load data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    loadProfile();
  }, [vendorId]);

  useEffect(() => {
    if (!vendorId) return;
    const loadOrders = async () => {
      try {
        setOrdersLoading(true);
        const ordersResponse = await fetchMedicalStoreOrders(vendorId);
        setOrderStats(ordersResponse);
      } catch (err) {
        console.error('Error loading orders:', err);
      } finally {
        setOrdersLoading(false);
      }
    };
    loadOrders();
  }, [vendorId]);

  useEffect(() => {
    if (!vendorId) return;
    getFeedbacks({ serviceType: 'MEDICINE', targetType: 'VENDOR', targetId: vendorId, limit: 10 })
      .then((r) => setVendorFeedback(r.data || []))
      .catch(() => setVendorFeedback([]));
  }, [vendorId]);

  const filteredOrders =
    orderStats?.orders.filter((order) => {
      const { name: customerName, phone: customerPhone } = getCustomerDisplay(order as OrderWithCustomerAlias);
      const oid = (order.orderId || '').toLowerCase();
      const matchesSearch =
        oid.includes(searchText.toLowerCase()) ||
        customerName.toLowerCase().includes(searchText.toLowerCase()) ||
        customerPhone.includes(searchText);
      const os = (order.orderStatus || '').toLowerCase();
      const matchesStatus = statusFilter === 'all' || os === statusFilter.toLowerCase();
      return matchesSearch && matchesStatus;
    }) || [];

  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / itemsPerPage));
  const paginatedOrders = filteredOrders.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, statusFilter]);

  const handleToggleStatus = async (vid: string) => {
    try {
      setUpdatingStatus(vid);
      const response = await toggleVendorStatus(vid);
      if (response.success) {
        setStoreData((prev) => {
          if (!prev) return null;
          return {
            ...prev,
            Vendor: {
              ...prev.Vendor,
              status: response.data.status as 'approved' | 'pending' | 'rejected',
            },
          };
        });
      }
    } catch (err) {
      console.error('Error toggling status:', err);
      setError('Failed to toggle status. Please try again later.');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const openCommissionModal = async () => {
    if (!vendorId) return;
    setIsCommissionModalOpen(true);
    setCommissionError(null);
    setCommissionLoading(true);
    try {
      const response = await fetchVendorCommissionRate(vendorId);
      setCommissionRateInput(String(response?.data?.commissionRate ?? 0));
      setReferralCommissionInput(String(response?.data?.referralCommission ?? 0));
    } catch (error: any) {
      setCommissionError(error?.response?.data?.message || 'Failed to load current commission rate');
      setCommissionRateInput('');
      setReferralCommissionInput('');
    } finally {
      setCommissionLoading(false);
    }
  };

  const closeCommissionModal = () => {
    if (commissionSaving) return;
    setIsCommissionModalOpen(false);
    setCommissionError(null);
    setCommissionRateInput('');
    setReferralCommissionInput('');
    setCommissionLoading(false);
  };

  const handleSaveCommissionRate = async () => {
    if (!vendorId || !storeData) return;
    const parsed = Number(commissionRateInput);
    const referralParsed = Number(referralCommissionInput);
    if (Number.isNaN(parsed) || parsed < 0 || parsed > 100) {
      setCommissionError('Please enter a valid commission rate between 0 and 100.');
      return;
    }
    if (Number.isNaN(referralParsed) || referralParsed < 0) {
      setCommissionError('Please enter a valid referral commission amount (0 or more).');
      return;
    }
    const confirmed = window.confirm(
      `Are you sure you want to update commission rate to ${parsed.toFixed(2)}% and referral commission to ₹${referralParsed.toFixed(2)} for ${storeData.name}?`
    );
    if (!confirmed) return;
    try {
      setCommissionSaving(true);
      setCommissionError(null);
      await updateVendorCommissionRate(vendorId, {
        commissionRate: parsed,
        referralCommission: referralParsed,
      });
      setStoreData((prev) => (prev ? { ...prev, commissionRate: parsed } : prev));
      closeCommissionModal();
    } catch (error: any) {
      setCommissionError(error?.response?.data?.message || 'Failed to update commission rate');
    } finally {
      setCommissionSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !storeData) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-600 dark:text-red-400">{error || 'Medical store data not found'}</p>
      </div>
    );
  }

  return (
    <div className="w-full min-w-0 max-w-7xl mx-auto p-3 sm:p-4 md:p-6">
      {/* Header — matches ambulance vendor dashboard */}
      <div className="mb-8 bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
        <div className="flex flex-col space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start space-x-4 min-w-0 flex-1">
            <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg shrink-0 mt-1">
              <BuildingStorefrontIcon className="h-8 w-8 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white truncate">{storeData.name}</h1>
              <div className="flex flex-col space-y-1.5 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-4 mt-2">
                <div className="flex items-center text-gray-600 dark:text-gray-400 min-w-0">
                  <MapPinIcon className="h-4 w-4 mr-1 shrink-0" />
                  <span className="text-sm truncate">
                    {[storeData.address, storeData.city, storeData.state].filter(val => val && val.trim() !== '').join(', ') || '—'}
                  </span>
                </div>
                <div className="flex items-center text-gray-600 dark:text-gray-400 shrink-0">
                  <PhoneIcon className="h-4 w-4 mr-1 shrink-0" />
                  <span className="text-sm">{storeData.contactNumber || '—'}</span>
                </div>
                <div className="flex items-center text-gray-600 dark:text-gray-400 min-w-0">
                  <EnvelopeIcon className="h-4 w-4 mr-1 shrink-0" />
                  <span className="text-sm truncate">{storeData.emailId || '—'}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center flex-wrap justify-end gap-2 shrink-0">
              <div className="relative flex items-center">
              <button
                type="button"
                onClick={() => setHeaderMenuOpen((o) => !o)}
                className="p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                title="Actions"
                aria-expanded={headerMenuOpen}
                aria-haspopup="true"
              >
                <EllipsisVerticalIcon className="h-6 w-6" />
              </button>
              {headerMenuOpen && (
                <>
                  <div className="fixed inset-0 z-10" aria-hidden="true" onClick={() => setHeaderMenuOpen(false)} />
                  <div className="absolute right-0 top-full mt-1 z-20 w-52 py-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
                    <button
                      type="button"
                      onClick={() => {
                        navigate(`/vendors/medical-store/${vendorId}/details`);
                        setHeaderMenuOpen(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                    >
                      <EyeIcon className="h-4 w-4" />
                      View Details
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setIsBankDetailsModalOpen(true);
                        setHeaderMenuOpen(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 flex items-center gap-2"
                    >
                      <BuildingStorefrontIcon className="h-4 w-4" />
                      Bank Details
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        openCommissionModal();
                        setHeaderMenuOpen(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 flex items-center gap-2"
                    >
                      <StarIcon className="h-4 w-4" />
                      Commission Rate
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        handleToggleStatus(storeData.Vendor.vendorId);
                        setHeaderMenuOpen(false);
                      }}
                      disabled={updatingStatus === storeData.Vendor.vendorId}
                      className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                        storeData.Vendor.status === 'approved'
                          ? 'text-yellow-700 dark:text-yellow-300 hover:bg-yellow-50 dark:hover:bg-yellow-900/20'
                          : 'text-green-700 dark:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20'
                      }`}
                    >
                      {updatingStatus === storeData.Vendor.vendorId ? (
                        <>
                          <ArrowPathIcon className="h-4 w-4 animate-spin" />
                          Updating...
                        </>
                      ) : storeData.Vendor.status === 'approved' ? (
                        <>
                          <ClockIcon className="h-4 w-4" />
                          Mark as Pending
                        </>
                      ) : (
                        <>
                          <CheckCircleIcon className="h-4 w-4" />
                          Mark as Approve
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4 pt-4 border-t dark:border-gray-700">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Status:</span>
              <span
                className={`px-2 py-1 text-xs font-medium rounded-full ${
                  storeData.Vendor.status === 'approved'
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                    : storeData.Vendor.status === 'pending'
                    ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                    : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                }`}
              >
                {storeData.Vendor.status.charAt(0).toUpperCase() + storeData.Vendor.status.slice(1)}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Active:</span>
              <span
                className={`px-2 py-1 text-xs font-medium rounded-full ${
                  storeData.Vendor.isActive
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                    : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                }`}
              >
                {storeData.Vendor.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-3">
            <button
              type="button"
              onClick={() => navigate(`/vendors/medical-store/${vendorId}/event-timeline`)}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <RectangleStackIcon className="h-5 w-5 text-teal-600 dark:text-teal-400" />
              Event timeline
            </button>
            <button
              type="button"
              onClick={() => navigate(`/vendors/medical-store/${vendorId}/login-activity`)}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <ShieldCheckIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
              Login activity
            </button>
          </div>
        </div>
      </div>

      {/* Core metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <MetricCard
          title="Total Orders"
          value={orderStats?.totalOrders ?? 0}
          icon={<CalendarIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />}
          description="All time orders"
        />
        <MetricCard
          title="Completed Orders"
          value={orderStats?.completedCount ?? 0}
          icon={<CheckCircleIcon className="h-6 w-6 text-green-600 dark:text-green-400" />}
          description="Successfully completed"
        />
        <MetricCard
          title="Cancelled Orders"
          value={orderStats?.cancelledCount ?? 0}
          icon={<XCircleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />}
          description="Cancelled orders"
        />
        <MetricCard
          title="Pending Orders"
          value={orderStats?.pendingCount ?? 0}
          icon={<ClockIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />}
          description="Awaiting completion"
        />
      </div>

      {/* Orders table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700 mb-8">
        <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Recent Orders</h2>
          <div className="flex flex-wrap gap-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search orders..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md pl-8 pr-3 py-1.5 text-sm w-48 md:w-64 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
              <MagnifyingGlassIcon className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-3 py-1.5 text-sm"
            >
              <option value="all">All Status</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
              <option value="pending">Pending</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Order ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Payment
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {ordersLoading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                    Loading…
                  </td>
                </tr>
              ) : (
                paginatedOrders.map((order, rowIndex) => {
                  const { name: displayName, phone: displayPhone } = getCustomerDisplay(order as OrderWithCustomerAlias);
                  const oid = order.orderId || '';
                  const paymentLabel = order.paymentStatus ?? '—';
                  const statusLabel = order.orderStatus ?? '—';
                  const normalizedRawStatus = String((order as Order & { status?: string }).status || '').toLowerCase();
                  const canViewInvoice = MEDICINE_INVOICE_ELIGIBLE_STATUSES.has(normalizedRawStatus);
                  return (
                    <tr
                      key={oid || `row-${rowIndex}`}
                      onClick={() => oid && vendorId && navigate(`/vendors/medical-store/${vendorId}/order/${encodeURIComponent(oid)}`)}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-xs font-mono text-gray-600 dark:text-gray-300 uppercase">
                          {oid ? `${oid.slice(0, 12)}${oid.length > 12 ? '…' : ''}` : '—'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">{displayName}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{displayPhone}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        ₹{(order.totalAmount || 0).toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${
                            statusLabel === 'Delivered'
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                              : statusLabel === 'Cancelled'
                              ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                              : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                          }`}
                        >
                          {statusLabel}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${
                            paymentLabel === 'Paid'
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                              : paymentLabel === 'Failed'
                              ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                              : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                          }`}
                        >
                          {paymentLabel}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {order.createdAt ? format(new Date(order.createdAt), 'dd MMM yyyy HH:mm') : '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {oid && canViewInvoice ? (
                          <button
                            type="button"
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                const invoiceBlob = await fetchVendorMedicineInvoiceBlob(oid);
                                const blobUrl = URL.createObjectURL(invoiceBlob);
                                window.open(blobUrl, '_blank', 'noopener,noreferrer');
                                // Cleanup object URL after tab has had time to load the PDF
                                window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
                              } catch (err) {
                                console.error('Failed to open invoice PDF:', err);
                                window.alert('Failed to open invoice. Please try again.');
                              }
                            }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100 dark:border-blue-800 dark:text-blue-300 dark:bg-blue-900/20 dark:hover:bg-blue-900/30"
                          >
                            <DocumentTextIcon className="h-4 w-4" />
                            View Invoice
                          </button>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Showing{' '}
            {filteredOrders.length === 0
              ? 0
              : (currentPage - 1) * itemsPerPage + 1}{' '}
            to {Math.min(currentPage * itemsPerPage, filteredOrders.length)} of {filteredOrders.length} results
          </div>
          <div className="flex space-x-2">
            <button
              type="button"
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1 || ordersLoading}
              className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <div className="flex items-center space-x-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  type="button"
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  disabled={ordersLoading}
                  className={`px-3 py-1 text-sm rounded-md ${
                    currentPage === page
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                  }`}
                >
                  {page}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage >= totalPages || ordersLoading}
              className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Bottom grid — aligned with ambulance dashboard */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Response Analytics</h2>
            <div className="py-8 text-center text-gray-500 dark:text-gray-400">Coming soon</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Order insights</h2>
            <div className="py-8 text-center text-gray-500 dark:text-gray-400">Coming soon</div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Financial Overview</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">No financial summary for this vendor yet.</p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">User Feedback</h2>
            <div className="space-y-4">
              {vendorFeedback.length > 0 ? (
                <>
                  <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <div className="flex items-center">
                      <StarIcon className="h-5 w-5 text-yellow-500 dark:text-yellow-400 mr-2" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">Average Rating</span>
                    </div>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {(vendorFeedback.reduce((s, f) => s + f.rating, 0) / vendorFeedback.length).toFixed(1)}/5
                    </span>
                  </div>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {vendorFeedback.slice(0, 5).map((f) => (
                      <div key={f.feedbackId} className="p-2 bg-gray-50 dark:bg-gray-700/50 rounded text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-700 dark:text-gray-300">{f.submitterName ?? '—'}</span>
                          <span className="text-yellow-600 dark:text-yellow-400">{f.rating}/5</span>
                        </div>
                        {f.review && <p className="text-gray-600 dark:text-gray-400 mt-1 truncate">{f.review}</p>}
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">No feedback for this vendor yet.</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {isCommissionModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-end justify-center overflow-y-auto bg-black/50 p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] sm:items-center sm:p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
            <div className="p-5 border-b dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Commission Rate</h3>
              <button onClick={closeCommissionModal} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                ×
              </button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-300">{storeData.name}</p>
              {commissionLoading ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">Loading current rate...</p>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Commission Rate (%)</label>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      step="0.01"
                      value={commissionRateInput}
                      onChange={(e) => setCommissionRateInput(e.target.value)}
                      className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Referral Commission Amount (INR)</label>
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={referralCommissionInput}
                      onChange={(e) => setReferralCommissionInput(e.target.value)}
                      className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                </>
              )}
              {commissionError && <p className="text-sm text-red-600 dark:text-red-400">{commissionError}</p>}
            </div>
            <div className="px-5 py-4 border-t dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={closeCommissionModal}
                disabled={commissionSaving}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveCommissionRate}
                disabled={commissionLoading || commissionSaving}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-60"
              >
                {commissionSaving ? 'Saving...' : 'Add / Update'}
              </button>
            </div>
          </div>
        </div>
      )}
      <VendorBankDetailsModal
        vendorId={vendorId || ''}
        vendorName={storeData?.name}
        isOpen={isBankDetailsModalOpen}
        onClose={() => setIsBankDetailsModalOpen(false)}
      />
    </div>
  );
};

export default MedicalStoreVendorDashboard;
